import { PLAN_ID_REGEX, isPlanId, stripPlanIdExtension } from './stringUtils';

/**
 * Semantics of the census "current plan" column in plan-id mode.
 *
 * Deliberately separate from the HIOS plan-id primitives in `stringUtils`:
 * those describe an actual catalog plan and are used to filter the plan
 * catalog itself, so they must stay strict. The MEDICARE sentinel below is a
 * census-only value that matches nothing in the catalog.
 */

/**
 * Stored in `census_employee_v2.current_plan_id` for a member whose current
 * coverage is Medicare rather than an ICHRA/ACA plan. It is not a HIOS id, so
 * it must be excluded before any plan-catalog lookup.
 */
export const MEDICARE_PLAN_ID = 'MEDICARE';

export function isMedicarePlanId(value?: string | null): boolean {
  if (!value) return false;
  return value.trim().toUpperCase() === MEDICARE_PLAN_ID;
}

/**
 * What the column may carry in plan-id mode: a HIOS plan id or the MEDICARE
 * sentinel. Built from {@link PLAN_ID_REGEX} so the two cannot drift.
 */
export const CENSUS_CURRENT_PLAN_ID_REGEX = new RegExp(
  `^(?:${PLAN_ID_REGEX.source.replace(/^\^/u, '').replace(/\$$/u, '')}|${MEDICARE_PLAN_ID})$`,
  'u'
);

/** True for any value that puts the census into plan-id mode. */
export function isCensusCurrentPlanId(value?: string | null): boolean {
  return isPlanId(value) || isMedicarePlanId(value);
}

/**
 * Canonical storage form: a plan id loses its extension suffix, the MEDICARE
 * sentinel is upper-cased.
 */
export function normalizeCensusCurrentPlanId(value: string): string {
  return isMedicarePlanId(value)
    ? MEDICARE_PLAN_ID
    : stripPlanIdExtension(value);
}
